import React, { useState, useEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import SailingIcon from "@mui/icons-material/Sailing";
import SendIcon from "@mui/icons-material/Send";
import { keyframes } from "@mui/system";

import { MAX_LENGTH_INPUT_SEARCH } from "../env.js";
import { record } from "aws-amplify/analytics";

// Define the glowing animation
const glowAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const InputQuery = ({
  onSubmitQuery,
  isLoading,
  suggestionQuestions,
  infoSuggestionQuestions,
  language = "en",
  borderRadius = 8,
}) => {
  const [query, setQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isBoatHovered, setIsBoatHovered] = useState(false);
  const [isSendHovered, setIsSendHovered] = useState(false);
  const isEnabled = query.trim() !== "" && !isLoading;

  const inputRef = useRef(null);

  useEffect(() => {
    // Focus the input element
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []); // Empty dependency array means this runs once on mount

  const handleQueryChange = (event) => {
    setQuery(event.target.value.replace("\n", ""));
  };

  const handleKeyPress = (event) => {
    if (event.code === "Enter" && isEnabled) {
      submitQuery();
    }
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    submitQuery();
  };

  const submitQuery = () => {
    if (isEnabled) {
      record({
        name: "assistant",
        attributes: {
          get: "answer",
        },
      });
      onSubmitQuery(query);
      setQuery("");
    }
  };

  const handleMenuQuestions = (event) => {
    if (!isLoading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenuQuestions = () => {
    setAnchorEl(null);
  };

  const handleExample = (value) => () => {
    if (!isLoading) {
      onSubmitQuery(value);
    }
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      {isInputFocused && (
        <Box
          sx={{
            position: "absolute",
            top: -3,
            left: -3,
            right: -3,
            bottom: -3,
            borderRadius: borderRadius,
            background:
              "linear-gradient(90deg, rgba(204,204,204,0.125) 0%, rgba(217,217,217,0.125) 35%, rgba(179,179,179,0.125) 75%, rgba(191,191,191,0.125) 100%)",
            backgroundSize: "200% 200%",
            filter: "blur(2px)",
            opacity: 1,
            zIndex: -1,
            animation: `${glowAnimation} 2s infinite ease-in-out`,
          }}
        />
      )}
      <Paper
        component="form"
        sx={(theme) => ({
          zIndex: 1,
          bgcolor: "white",
          px: "8px",
          py: "8px",
          display: "flex",
          alignItems: "center",
          boxShadow: isInputFocused
            ? "none"
            : "rgba(128, 128, 128, 0.025) 0px 6px 24px 0px, rgba(128, 128, 128, 0.04) 0px 0px 0px 1px",
          borderRadius: borderRadius,
          transition: "box-shadow 0.3s ease-in-out",
          position: "relative",
          border: isInputFocused ? "0" : "1px solid",
          borderColor: isInputFocused ? "transparent" : "divider",
          ...(isInputFocused && {
            padding: "calc(1px + 0.5rem)",
          }),

          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: borderRadius,
            padding: "2px",
            background: isInputFocused
              ? "linear-gradient(90deg, rgba(217,217,217,0.25) 0%, rgba(204,204,204,0.25) 35%, rgba(179,179,179,0.25) 75%, rgba(191,191,191,0.25) 100%)"
              : "transparent",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            pointerEvents: "none",
            zIndex: 0,
          },
        })}
      >
        <Box>
          <IconButton
            sx={{
              p: 0,
              ml: 0.5,
              mr: 0.5,
              color: isBoatHovered
                ? "primary.main"
                : isInputFocused
                ? "primary.light"
                : "text.secondary",
              transition: "color 0.3s ease",
            }}
            data-amplify-analytics-on="click"
            data-amplify-analytics-name="click"
            data-amplify-analytics-attrs="button:sailing"
            aria-label="suggestions"
            onClick={handleMenuQuestions}
            disabled={isLoading}
            onMouseEnter={() => setIsBoatHovered(true)}
            onMouseLeave={() => setIsBoatHovered(false)}
          >
            <SailingIcon />
          </IconButton>

          <Menu
            id="suggestions-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenuQuestions}
          >
            {suggestionQuestions &&
              suggestionQuestions[language] &&
              suggestionQuestions[language].map((question, index) => (
                <MenuItem
                  key={`question-${index}`}
                  data-amplify-analytics-on="click"
                  data-amplify-analytics-name="click"
                  data-amplify-analytics-attrs="button:example"
                  onClick={handleExample(question)}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: "text.disabled",
                      mr: 1.5,
                      minWidth: "20px",
                      fontSize: "0.875rem",
                    }}
                  >
                    {index + 1}
                  </Box>
                  {question}
                </MenuItem>
              ))}
          </Menu>
        </Box>

        <InputBase
          required
          inputRef={inputRef}
          id="query"
          name="query"
          placeholder="Type your question..."
          fullWidth
          multiline
          onChange={handleQueryChange}
          onKeyDown={handleKeyPress}
          value={query}
          variant="outlined"
          inputProps={{ maxLength: MAX_LENGTH_INPUT_SEARCH }}
          sx={{ pl: 1, pr: 2, pt: 1 }}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />
        <Divider sx={{ height: 32 }} orientation="vertical" />

        <IconButton
          color="secondary"
          sx={{
            p: 1,
            color:
              isEnabled && isSendHovered
                ? "secondary.main"
                : isInputFocused
                ? "secondary.main"
                : "text.secondary",
            transition: "color 0.3s ease",
          }}
          aria-label="send"
          disabled={!isEnabled}
          onClick={handleSubmitClick}
          onMouseEnter={() => setIsSendHovered(true)}
          onMouseLeave={() => setIsSendHovered(false)}
          data-amplify-analytics-on="click"
          data-amplify-analytics-name="click"
          data-amplify-analytics-attrs="button:send"
        >
          <SendIcon />
        </IconButton>
      </Paper>
    </Box>
  );
};

export default InputQuery;
